import { Separator } from "@/components/ui/separator";
import { Phone, Mail, MapPin, Building2 } from "lucide-react";
import Link from "next/link";
import { FaInstagram, FaTiktok } from "react-icons/fa6";

export default function Footer() {
  const venueUrl =
    process.env.NEXT_PUBLIC_ADMIN_URL || "https://admin.firstin.app";
  return (
    <footer className="bg-gradient-to-b from-black to-zinc-900 border-t border-zinc-800 py-16">
      <div className="container mx-auto px-4">
        {/* CTA Banner for Venue Owners */}
        <div className="mb-12 p-6 bg-zinc-800/50 rounded-lg backdrop-blur-sm border border-zinc-700 shadow-xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h2 className="text-xl md:text-2xl font-bold mb-2">
                <span className="text-white">Own a Venue?</span>
              </h2>
              <p className="text-zinc-300 max-w-md">
                Partner with{" "}
                <span className="font-bold text-white">FirstIn</span> and reach
                more customers. Boost your venue&apos;s visibility and revenue.
              </p>
            </div>
            <Link
              href={venueUrl}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black hover:bg-gray-100 rounded-md font-medium transition-all transform hover:scale-105 shadow-lg hover:shadow-gray-500/30"
            >
              <Building2 size={18} />
              <span>Get Started as a Venue</span>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Logo and About */}
          <div className="flex flex-col">
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-3">
                <span className="text-white">FirstIn</span>
              </h2>
              <p className="text-zinc-400 text-sm">
                Elevating Toronto&apos;s nightlife experience with exclusive
                access to premier venues.
              </p>
            </div>

            <h3 className="text-base font-medium mb-3 text-zinc-300">
              Connect with Us
            </h3>
            <div className="flex space-x-4">
              <Link
                href="https://www.instagram.com/firstin.app"
                className="flex items-center justify-center w-9 h-9 rounded-full bg-zinc-800 hover:bg-zinc-700 transition-colors"
                aria-label="Instagram"
              >
                <FaInstagram size={18} />
              </Link>
              <Link
                href="https://www.tiktok.com/@firstin.app"
                className="flex items-center justify-center w-9 h-9 rounded-full bg-zinc-800 hover:bg-zinc-700 transition-colors"
                aria-label="TikTok"
              >
                <FaTiktok size={18} />
              </Link>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-medium mb-4">Quick Links</h3>
            <nav className="flex flex-col space-y-2">
              <Link
                href="/"
                className="text-zinc-400 hover:text-white transition-colors"
              >
                Events
              </Link>
              <Link
                href="/users/tickets"
                className="text-zinc-400 hover:text-white transition-colors"
              >
                My Tickets
              </Link>
              <Link
                href="/contact"
                className="text-zinc-400 hover:text-white transition-colors"
              >
                Contact Us
              </Link>
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-medium mb-4">Contact</h3>
            <div className="space-y-3">
              <div className="flex items-center group">
                <Phone
                  size={18}
                  className="mr-3 text-zinc-500 group-hover:text-white transition-colors"
                />
                <span className="text-zinc-400 group-hover:text-white transition-colors">
                  +1 437-269-2245
                </span>
              </div>
              <div className="flex items-center group">
                <Mail
                  size={18}
                  className="mr-3 text-zinc-500 group-hover:text-white transition-colors"
                />
                <a
                  href="mailto:Firstin.ca@outlook.com"
                  className="text-zinc-400 group-hover:text-white transition-colors"
                >
                  Firstin.ca@outlook.com
                </a>
              </div>
              <div className="flex items-center group">
                <MapPin
                  size={18}
                  className="mr-3 text-zinc-500 group-hover:text-white transition-colors"
                />
                <span className="text-zinc-400 group-hover:text-white transition-colors">
                  Toronto, Ontario, Canada
                </span>
              </div>
            </div>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-medium mb-4">Legal</h3>
            <div className="space-y-2">
              <Link
                href="/privacy"
                className="block text-zinc-400 hover:text-white transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="block text-zinc-400 hover:text-white transition-colors"
              >
                Terms of Use
              </Link>
              <Link
                href="/refund-policy"
                className="block text-zinc-400 hover:text-white transition-colors"
              >
                Refund Policy
              </Link>
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-zinc-700/50" />

        <div className="flex flex-col md:flex-row justify-between items-center text-sm text-zinc-500">
          <p>© 2025 FirstIn. All rights reserved.</p>
          <p className="mt-2 md:mt-0">
            Designed with <span className="text-red-500">♥</span> in Toronto
          </p>
        </div>
      </div>
    </footer>
  );
}
